import { useState, useLayoutEffect, useRef } from "react";

import { Button, InfoModal, LoadingSpinner, Toast } from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import globalToasterRef from "@/base/globalToasterRef";
import schoolQueryKey from "@/constants/SCHOOL_REGISTRATION_QUERY_KEYS";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import useUserPost from "@/users/useUserPost";
import useRemoveSchoolQueryParam from "@/utils/useRemoveSchoolQueryParam";
import useRemoveTeacherSchool from "@/utils/useRemoveTeacherSchool";
import ConfirmEmailAddress from "./ConfirmEmailAddress";
import { modalCss } from "./ChangeSchoolModal.style";
import AcceptChangeSchoolTerm from "./AcceptChangeSchoolTerm";

const propTypes = {
  /**
   * User information
   */
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  /**
   * Modal props from useModal
   */
  modalProps: PropTypes.object.isRequired,
  /**
   * setActive func from useModal
   */
  setActive: PropTypes.func.isRequired,
};

const { CONFIRMATION_KEY } = schoolQueryKey;

const EVENT_TOAST_ERROR = {
  showIcon: true,
  statusColor: Toast.statusColor.danger,
  children: "Update failed",
};

const EVENT_TOAST_SUCCESS = {
  showIcon: true,
  statusColor: Toast.statusColor.success,
  children: "School changed",
};

const ChangeSchoolModal = ({ user, modalProps, setActive }) => {
  const router = useRouter();
  const [updateUser] = useUserPost();
  const [form, setForm] = useState({});
  const [slideIndex, setSlideIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const closeButtonRef = useRef();

  useRemoveSchoolQueryParam(() =>
    globalToasterRef.current.displayToast(EVENT_TOAST_SUCCESS),
  );

  useLayoutEffect(() => {
    closeButtonRef?.current?.focus();
  }, [slideIndex]);

  const onClose = () => {
    setSlideIndex(0);
    setForm({});
  };

  const removeTeacherSchool = useRemoveTeacherSchool(user);

  const updateUserEmail = async () => {
    const response = await updateUser({
      id: user.id,
      email: form.newEmail,
      send_verification_email: true,
    });
    if (response.error) throw response.error;
    return response;
  };

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      await removeTeacherSchool();
      if (!form.isCurrentEmailValid && form.newEmail) await updateUserEmail();
      const nextUrl = new URL(router.pathname, window.location);
      nextUrl.searchParams.append(CONFIRMATION_KEY, "true");
      window.location.href = `/registration/school-search/?next=${nextUrl.toString()}`;
    } catch {
      globalToasterRef.current.displayToast(EVENT_TOAST_ERROR);
      setIsSubmitting(false);
    }
  };

  const onSubmitWithGoogle = async () => {
    try {
      setIsSubmitting(true);
      await removeTeacherSchool();
      const params = new URLSearchParams({
        source: "sign-up",
        role: "teacher",
        next: "/join/",
      });
      window.location.href = `${MONOLITH_ORIGIN}/login/google-oauth2/?${params.toString()}`;
    } catch {
      globalToasterRef.current.displayToast(EVENT_TOAST_ERROR);
      setIsSubmitting(false);
    }
  };

  const slides = [
    {
      isNextEnabled: form.acceptTerm,
      Component: (
        <AcceptChangeSchoolTerm
          isChecked={form.acceptTerm}
          setChecked={(acceptTerm) =>
            setForm((form) => ({ ...form, acceptTerm }))
          }
        />
      ),
    },
    {
      isNextEnabled:
        form.isCurrentEmailValid || (form.newEmail && form.isNewEmailValid),
      Component: (
        <ConfirmEmailAddress
          user={user}
          onChange={(values) => setForm((form) => ({ ...form, ...values }))}
          form={form}
          onSubmit={onSubmitWithGoogle}
        />
      ),
    },
  ];

  const Footer = (
    <>
      <Button
        flavor={Button.flavors.secondary}
        onClick={() =>
          slideIndex === 0 ? setActive(false) : setSlideIndex(slideIndex - 1)
        }
        disabled={isSubmitting}
      >
        {slideIndex === 0 ? "Cancel" : "Back"}
      </Button>
      <Button
        flavor={Button.flavors.primary}
        disabled={!slides[slideIndex].isNextEnabled || isSubmitting}
        onClick={() =>
          slideIndex < slides.length - 1
            ? setSlideIndex(slideIndex + 1)
            : onSubmit()
        }
      >
        Next
      </Button>
    </>
  );

  return (
    <InfoModal
      eyebrow={`Step ${slideIndex + 1} of ${slides.length}`}
      labelId="ChangeSchool"
      onClose={onClose}
      title="Change school?"
      __cssFor={modalCss}
      footer={Footer}
      closeButtonProps={{ ref: closeButtonRef }}
      {...modalProps}
    >
      {isSubmitting ? <LoadingSpinner /> : slides[slideIndex].Component}
    </InfoModal>
  );
};

ChangeSchoolModal.propTypes = propTypes;

export default ChangeSchoolModal;
